import { overwriteReactiveObject } from "@/core/util/overwriteReactiveObject";
import { useStackRouter } from "@/packages/stack-router/hooks/useStackRouter";
import { StackRouterResolveResult, TRouteStackConfig } from "@/packages/stack-router/types";
import { h, reactive } from "vue";
import { RouteLocationNormalized, useRoute } from "vue-router";

async function resolveComponent(component: any) {
    if (!component)
        return component
    if (component.then) {
        component = await component
    }
    return component.default ? component.default : component
}


export type RouteStackableComponent = {
    _isStackable: boolean
    getStackConfig: () => any
    initialize: (to: RouteLocationNormalized, lastResolved: StackRouterResolveResult) => any
    isInitialized: () => boolean
    setup: any
    setProps: (props: any) => void
}

const resolveCallbacks = (data: any, ctx: any, exclude: any) => {
    const cdata: any = {}

    for (const [key, value] of Object.entries(data)) {
        if (!exclude || exclude.indexOf(key) === -1) {
            cdata[key] = typeof value === 'function' ? value(ctx) : value
        } else {
            cdata[key] = value
        }
    }

    return cdata
}

export const useStackRouteComponent = (stackComponent: any, config: TRouteStackConfig = {}): RouteStackableComponent => {

    let stackConfig: TRouteStackConfig = {}

    let initialized = false

    let props = reactive<any>({})

    async function initialize(to: RouteLocationNormalized, lastResolved: StackRouterResolveResult): Promise<TRouteStackConfig> {

        initialized = true

        const stackRouter = useStackRouter()

        let stackGlobalConfig = stackRouter.getStack()?.config || {}

        let stackConfigLocal: TRouteStackConfig = {
            ...stackGlobalConfig
        }

        let stackProps: any = {}

        const toParams = {
            ...to.params,
            ...to.query,
        }

        // Резолвим stackComponent если это функция (async import)
        const resolvedStackComponent = typeof stackComponent === 'function' 
            ? await resolveComponent(stackComponent())
            : stackComponent

        const nestedConfigs: TRouteStackConfig[] = [
            ...to.matched.filter(record => !!record.meta.stack).map(record => record.meta.stack as TRouteStackConfig),
            config,
            {
                stackComponent: resolvedStackComponent,
                props: toParams
            }
        ]

        nestedConfigs.forEach((nestedConfig) => {
            stackConfigLocal = {
                ...stackConfigLocal,
                ...nestedConfig
            }
            stackProps = {
                ...stackProps,
                ...(nestedConfig.props || {})
            }
        })

        stackConfigLocal.props = stackProps

        // Резолвим callback-функции, но исключаем компоненты (они обрабатываются отдельно)
        stackConfig = resolveCallbacks(stackConfigLocal, {}, ['stackComponent', 'routeComponent', 'backComponent']) as TRouteStackConfig

        // Резолвим компоненты: если это функция - вызываем и резолвим промис
        stackConfig.stackComponent = await resolveComponent(
            typeof stackConfig.stackComponent === 'function' 
                ? stackConfig.stackComponent() 
                : stackConfig.stackComponent
        )
        
        stackConfig.backComponent = await resolveComponent(
            typeof stackConfig.backComponent === 'function' 
                ? stackConfig.backComponent() 
                : stackConfig.backComponent
        )
        
        stackConfig.routeComponent = await resolveComponent(
            typeof stackConfig.routeComponent === 'function' 
                ? stackConfig.routeComponent() 
                : stackConfig.routeComponent
        )

        if (!lastResolved && stackConfig.routeComponent) {
            stackConfig.component = stackConfig.routeComponent
        } else {
            stackConfig.component = stackConfig.backComponent
        }

        overwriteReactiveObject(props, stackProps)

        return stackConfig
    }

    return {
        _isStackable: true,
        getStackConfig: () => {
            return stackConfig
        },
        setProps: (newProps: any) => {
            Object.assign(props, newProps)
        },
        isInitialized: () => {
            return initialized
        },
        initialize,
        setup: () => {
            const route = useRoute()
            return () => {
                if (!stackConfig.component) return null
                return h(stackConfig.component, {
                    ...props,
                    ...route.params,
                    ...route.query,
                });
            }
        }
    }
}


